import styled, { createGlobalStyle } from "styled-components";
import Modal from "@iso/components/Feedback/Modal";
import WithDirection from "@iso/lib/helpers/rtl";
import AsideImage from "@iso/assets/images/background/signin.png";

// Google Places renders its suggestion list on <body>; keep it above the
// full-screen modal so address autocomplete stays usable.
export const OnboardingGlobalStyle = createGlobalStyle`
  .pac-container {
    z-index: 1100;
  }
`;

const WelcomeModalWrapper = styled(Modal)`
  --ob-bg: #10412b;
  --ob-surface: #0b2c1d;
  --ob-border: rgba(255, 255, 255, 0.16);
  --ob-border-strong: rgba(255, 255, 255, 0.55);
  --ob-text: #ffffff;
  --ob-muted: rgba(255, 255, 255, 0.72);
  --ob-subtle: rgba(255, 255, 255, 0.45);
  --ob-btn: #eef0ee;
  --ob-btn-text: #0b2a1b;
  --ob-error: #ffa39a;

  top: 0;
  margin: 0;
  padding-bottom: 0;
  max-width: 100vw;

  .ant-modal-content {
    min-height: 100vh;
    border-radius: 0;
    box-shadow: none;
    background: var(--ob-bg);
    color: var(--ob-text);
  }
  .ant-modal-body {
    padding: 0;
  }

  /* ---------- Layout ---------- */
  .ob-page {
    display: flex;
    gap: 24px;
    min-height: 100vh;
    padding: 24px;
  }
  .ob-main {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 24px 48px;
  }
  .ob-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .ob-logo {
    height: 36px;
    filter: brightness(0) invert(1);
  }
  .ob-skip,
  .ob-back,
  .ob-link {
    background: none;
    border: 0;
    padding: 0;
    color: var(--ob-muted);
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      color: var(--ob-text);
      text-decoration: underline;
    }
  }
  .ob-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    max-width: 480px;
    padding: 48px 0 32px;
  }
  .ob-page.is-wide .ob-content {
    max-width: 1080px;
  }
  .ob-title {
    margin: 0 0 32px;
    color: var(--ob-text);
    font-size: 40px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.5px;
    &:focus {
      outline: none;
    }
  }
  .ob-lead {
    margin: -16px 0 28px;
    color: var(--ob-muted);
    font-size: 16px;
    line-height: 1.5;
  }
  .ob-note {
    margin: 16px 0 0;
    color: var(--ob-muted);
    font-size: 14px;
    line-height: 1.5;
    a {
      color: var(--ob-text);
      text-decoration: underline;
    }
  }
  .ob-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 24px;
    margin-top: 32px;
  }
  .ob-error {
    margin-bottom: 20px;
    padding: 12px 16px;
    border: 1px solid var(--ob-error);
    border-radius: 8px;
    color: var(--ob-error);
    font-size: 14px;
  }

  .ob-aside {
    position: relative;
    flex: 0 0 40%;
    max-width: 640px;
    overflow: hidden;
    border-radius: 16px;
    background: url(${AsideImage}) center / cover no-repeat;
    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.05) 45%,
        rgba(0, 0, 0, 0.55) 100%
      );
    }
  }
  .ob-aside-logo {
    position: absolute;
    z-index: 1;
    left: 50%;
    bottom: 56px;
    width: 60%;
    max-width: 280px;
    transform: translateX(-50%);
    filter: brightness(0) invert(1);
  }

  /* ---------- Gold trial offer (free-plan users, last step) ---------- */
  .ob-aside.ob-aside-offer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    padding: 48px 40px;
    background: #0b2c1d;
    &::after {
      display: none;
    }
  }
  .ob-aside-offer-logo {
    width: 55%;
    max-width: 300px;
    filter: brightness(0) invert(1);
  }
  .ob-trial-card {
    width: 100%;
    max-width: 480px;
    padding: 28px;
    border-radius: 16px;
    background: #e9ecea;
    color: var(--ob-btn-text);
    h2 {
      margin: 0 0 12px;
      color: var(--ob-btn-text);
      font-size: 32px;
      font-weight: 700;
    }
    p {
      margin: 0;
      color: #4a5a52;
      font-size: 14px;
      line-height: 1.55;
    }
  }
  .ob-trial-price {
    margin: 28px 0 16px;
    color: var(--ob-btn-text);
    font-size: 36px;
    font-weight: 700;
    line-height: 1.1;
    small {
      margin-left: 8px;
      color: #6b7a72;
      font-size: 14px;
      font-weight: 400;
    }
  }
  .ant-btn.ob-trial-btn {
    width: 100%;
    height: 44px;
    border: 0;
    border-radius: 8px;
    background: #10291d;
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    &:hover,
    &:focus {
      background: #1c3d2c;
      color: #ffffff;
    }
  }
  .ob-trial-card p.ob-trial-started {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--ob-btn-text);
    font-size: 15px;
    font-weight: 600;
  }
  .ob-trial-inline {
    display: none;
    margin-top: 32px;
  }
  @media only screen and (max-width: 991px) {
    .ob-trial-inline {
      display: block;
    }
    .ob-aside.ob-aside-offer {
      display: none;
    }
  }

  /* ---------- Buttons ---------- */
  .ant-btn.ob-btn-primary,
  .ant-btn.ob-btn-secondary {
    height: 56px;
    min-width: 190px;
    padding: 0 28px;
    border-radius: 8px;
    font-size: 17px;
    font-weight: 500;
  }
  .ant-btn.ob-btn-primary {
    background: var(--ob-btn);
    border-color: var(--ob-btn);
    color: var(--ob-btn-text);
    &:hover,
    &:focus {
      background: #ffffff;
      border-color: #ffffff;
      color: var(--ob-btn-text);
    }
  }
  .ant-btn.ob-btn-secondary {
    background: transparent;
    border-color: var(--ob-border-strong);
    color: var(--ob-text);
    &:hover,
    &:focus {
      background: rgba(255, 255, 255, 0.08);
      border-color: var(--ob-text);
      color: var(--ob-text);
    }
  }

  /* ---------- Form controls ---------- */
  .ant-form-item {
    margin-bottom: 20px;
  }
  .ant-form-item-label > label {
    color: var(--ob-text);
    font-size: 15px;
    font-weight: 400;
  }
  .ant-form-item-label > label.ant-form-item-required::before {
    display: none;
  }
  .ant-form-item-explain,
  .ant-form-item-extra {
    margin-top: 4px;
    font-size: 13px;
  }
  .ant-form-item-extra {
    color: var(--ob-muted);
  }
  .ant-form-item-has-error .ant-form-item-explain {
    color: var(--ob-error);
  }
  /* Scoped under .ob-page .ant-form-item so it outranks the global
     ".ant-modal-content .ant-modal-body ... input" rule. */
  .ob-page .ant-form-item input.ant-input {
    height: 56px;
    padding: 0 16px;
    border-radius: 8px;
    background: var(--ob-surface);
    border: 1px solid var(--ob-border);
    color: var(--ob-text);
    font-size: 15px;
    &::placeholder {
      color: var(--ob-subtle);
    }
    &:hover,
    &:focus {
      border-color: var(--ob-border-strong);
    }
    &:focus {
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12);
    }
  }
  .ob-page .ant-form-item-has-error input.ant-input,
  .ob-page .ant-form-item-has-error input.ant-input:hover,
  .ob-page .ant-form-item-has-error input.ant-input:focus {
    background: var(--ob-surface);
    border-color: var(--ob-error);
  }

  .ob-radio-list {
    display: grid;
    gap: 16px;
  }
  .ant-radio-wrapper {
    display: inline-flex;
    align-items: center;
    margin: 0;
    color: var(--ob-text);
    white-space: normal;
    > span:last-child {
      font-size: 16px;
      font-weight: 400;
    }
  }
  .ant-radio {
    top: 0;
  }
  .ant-radio-inner {
    width: 20px;
    height: 20px;
    background: var(--ob-surface);
    border-color: var(--ob-border-strong);
    /* antd centres the dot itself (top/left 50% + negative margins);
       only its colour is overridden here. */
    &::after {
      background: var(--ob-btn-text);
    }
  }
  .ant-radio-checked .ant-radio-inner {
    background: var(--ob-btn);
    border-color: var(--ob-btn);
    &::after {
      transform: scale(0.57);
    }
  }
  .ant-radio-wrapper:hover .ant-radio-inner,
  .ant-radio-input:focus + .ant-radio-inner {
    border-color: var(--ob-text);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.12);
  }
  .ant-radio-checked::after {
    border-color: var(--ob-btn);
  }

  /* ---------- Account type choice cards ---------- */
  .ob-choices {
    display: grid;
    gap: 14px;
  }
  .ob-choice {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 18px 20px;
    border: 1px solid var(--ob-border);
    border-radius: 12px;
    background: var(--ob-surface);
    cursor: pointer;
    transition: border-color 0.15s ease;
    &:hover {
      border-color: var(--ob-border-strong);
    }
    &.is-selected {
      border-color: var(--ob-text);
    }
    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }
    &:focus-within {
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.18);
    }
  }
  .ob-choice-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 1px solid var(--ob-border);
    border-radius: 10px;
    color: var(--ob-text);
    font-size: 18px;
  }
  .ob-choice.is-selected .ob-choice-icon {
    background: var(--ob-btn);
    border-color: var(--ob-btn);
    color: var(--ob-btn-text);
  }
  .ob-choice-text {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      margin-bottom: 2px;
      color: var(--ob-text);
      font-size: 15px;
      font-weight: 600;
    }
    span {
      color: var(--ob-muted);
      font-size: 13px;
      font-weight: 400;
      line-height: 1.45;
    }
  }
  .ob-choice-dot {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 1px solid var(--ob-border-strong);
    border-radius: 50%;
  }
  .ob-choice.is-selected .ob-choice-dot {
    border: 5px solid var(--ob-btn);
    background: var(--ob-btn-text);
  }
  /* ---------- Join request status ---------- */
  .ob-status-list {
    display: grid;
    gap: 12px;
  }
  .ob-status {
    display: flex;
    gap: 16px;
    padding: 20px;
    border: 1px solid var(--ob-border);
    border-radius: 12px;
    background: var(--ob-surface);
    p {
      margin: 0;
      color: var(--ob-muted);
      font-size: 15px;
      line-height: 1.5;
    }
    strong {
      color: var(--ob-text);
    }
  }
  .ob-status-icon {
    color: var(--ob-text);
    font-size: 22px;
    line-height: 1;
  }

  /* ---------- Get-ready option list ---------- */
  .ob-option-list {
    border: 1px solid var(--ob-border);
    border-radius: 12px;
    background: var(--ob-surface);
  }
  .ob-option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    & + .ob-option {
      border-top: 1px solid var(--ob-border);
    }
  }
  .ob-option-text {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      margin-bottom: 2px;
      color: var(--ob-text);
      font-size: 15px;
      font-weight: 600;
    }
    span {
      color: var(--ob-muted);
      font-size: 14px;
      line-height: 1.45;
    }
  }
  .ant-btn.ob-option-btn {
    flex-shrink: 0;
    height: 40px;
    border-radius: 8px;
    background: transparent;
    border-color: var(--ob-border-strong);
    color: var(--ob-text);
    &:hover,
    &:focus {
      background: rgba(255, 255, 255, 0.08);
      border-color: var(--ob-text);
      color: var(--ob-text);
    }
    &[disabled],
    &[disabled]:hover {
      background: transparent;
      border-color: var(--ob-border);
      color: var(--ob-muted);
    }
  }

  /* ---------- Plan cards ---------- */
  .ob-plan-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 24px;
    margin-bottom: 28px;
    .ob-title {
      margin-bottom: 0;
    }
  }
  .ob-billing {
    display: inline-flex;
    padding: 4px;
    border: 1px solid var(--ob-border);
    border-radius: 10px;
    background: var(--ob-surface);
  }
  .ob-billing-option {
    padding: 8px 18px;
    border: 0;
    border-radius: 7px;
    background: none;
    color: var(--ob-muted);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:hover:not([disabled]),
    &:focus-visible {
      color: var(--ob-text);
    }
    &.is-active {
      background: var(--ob-btn);
      color: var(--ob-btn-text);
    }
    &[disabled] {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
  .ob-plan-saving {
    margin-top: 6px;
    color: var(--ob-muted);
    font-size: 13px;
  }
  .ob-plans {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
  .ob-plan {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 24px;
    border: 1px solid var(--ob-border);
    border-radius: 16px;
    background: var(--ob-surface);
    cursor: pointer;
    transition: border-color 0.15s ease;
    &:hover {
      border-color: var(--ob-border-strong);
    }
    &.is-selected {
      border-color: var(--ob-text);
    }
  }
  /* Locked after a purchase: the cards are information only. */
  .ob-plans.is-locked .ob-plan {
    cursor: default;
    &:hover {
      border-color: var(--ob-border);
    }
  }
  .ob-plan-eyebrow {
    color: var(--ob-subtle);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
  }
  .ob-plan-name {
    margin: 4px 0 20px;
    color: var(--ob-text);
    font-size: 22px;
    font-weight: 700;
  }
  .ob-plan-price {
    color: var(--ob-text);
    font-size: 32px;
    font-weight: 700;
    line-height: 1.1;
    small {
      margin-left: 6px;
      color: var(--ob-subtle);
      font-size: 14px;
      font-weight: 400;
    }
  }
  .ob-plan-features {
    flex: 1;
    margin: 20px 0 24px;
    padding: 20px 0 0;
    border-top: 1px solid var(--ob-border);
    list-style: none;
    li {
      display: flex;
      gap: 10px;
      margin-bottom: 10px;
      color: var(--ob-muted);
      font-size: 14px;
      line-height: 1.45;
      .anticon {
        margin-top: 4px;
        color: var(--ob-text);
        font-size: 12px;
      }
    }
  }
  .ant-btn.ob-plan-select {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    background: transparent;
    border-color: var(--ob-border-strong);
    color: var(--ob-text);
    font-weight: 600;
    &:hover,
    &:focus {
      border-color: var(--ob-text);
      color: var(--ob-text);
      background: rgba(255, 255, 255, 0.08);
    }
    &[disabled],
    &[disabled]:hover {
      background: transparent;
      border-color: var(--ob-border);
      color: var(--ob-subtle);
    }
  }
  .ob-plan.is-selected .ant-btn.ob-plan-select {
    background: var(--ob-btn);
    border-color: var(--ob-btn);
    color: var(--ob-btn-text);
  }

  /* ---------- Stepper ---------- */
  .ob-stepper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 28px;
    ol {
      display: flex;
      align-items: center;
      margin: 0;
      padding: 0;
      list-style: none;
    }
    li {
      display: flex;
      align-items: center;
    }
    li + li::before {
      content: "";
      width: 48px;
      height: 1px;
      background: rgba(255, 255, 255, 0.6);
    }
  }
  .ob-step-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    color: var(--ob-text);
    font-size: 14px;
    font-weight: 500;
  }
  .is-done .ob-step-dot,
  .is-current .ob-step-dot {
    background: var(--ob-btn);
    border-color: var(--ob-btn);
    color: var(--ob-btn-text);
  }
  .is-current .ob-step-dot {
    font-weight: 700;
  }
  .ob-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  /* ---------- Responsive ---------- */
  @media only screen and (max-width: 991px) {
    .ob-page {
      padding: 16px;
    }
    .ob-main {
      padding: 8px 0;
    }
    .ob-aside {
      display: none;
    }
    .ob-title {
      font-size: 30px;
    }
  }
  @media only screen and (max-width: 991px) {
    .ob-plans {
      grid-template-columns: minmax(0, 1fr);
      gap: 24px;
    }
  }
  /* Short screens (laptops, browsers with toolbars): tighten the vertical
     rhythm so a full step, stepper included, fits without scrolling. */
  @media only screen and (min-width: 992px) and (max-height: 960px) {
    .ob-plan-header {
      margin-bottom: 18px;
    }
    .ob-page {
      padding: 16px;
    }
    .ob-main {
      padding: 12px 40px;
    }
    .ob-content {
      padding: 20px 0 16px;
    }
    .ob-title {
      margin-bottom: 20px;
      font-size: 32px;
    }
    .ob-lead {
      margin: -8px 0 20px;
    }
    .ob-choices {
      gap: 10px;
    }
    .ob-choice {
      padding: 12px 16px;
    }
    .ob-choice-icon {
      width: 40px;
      height: 40px;
    }
    .ob-actions {
      margin-top: 20px;
    }
    .ant-btn.ob-btn-primary,
    .ant-btn.ob-btn-secondary {
      height: 48px;
    }
    .ob-plan {
      padding: 18px 20px;
    }
    .ob-plan-name {
      margin-bottom: 8px;
    }
    .ob-plan-price {
      font-size: 28px;
    }
    .ob-plan-features {
      margin: 14px 0 16px;
      padding-top: 14px;
      li {
        margin-bottom: 6px;
        line-height: 1.35;
      }
    }
    .ob-note {
      margin-top: 10px;
    }
    .ob-step-dot {
      width: 36px;
      height: 36px;
    }
  }
  /* Small laptop screens (~768px and under): drop the decorative extras. */
  @media only screen and (min-width: 992px) and (max-height: 800px) {
    .ob-header {
      margin-bottom: -8px;
    }
    .ob-plan-header {
      margin-bottom: 12px;
    }
    .ob-plan-saving {
      margin-top: 2px;
      font-size: 12px;
    }
    .ob-content {
      padding: 12px 0 8px;
    }
    .ob-title {
      margin-bottom: 14px;
      font-size: 28px;
    }
    .ob-plan-eyebrow {
      display: none;
    }
    .ob-plan-features li {
      margin-bottom: 4px;
      font-size: 13px;
    }
    .ant-btn.ob-plan-select {
      height: 38px;
    }
    .ob-choice-text strong {
      font-size: 14px;
    }
  }
  @media only screen and (max-width: 575px) {
    .ob-content {
      padding-top: 32px;
    }
    .ant-btn.ob-btn-primary,
    .ant-btn.ob-btn-secondary {
      width: 100%;
    }
    .ob-option {
      flex-wrap: wrap;
    }
    .ob-stepper li + li::before {
      width: 20px;
    }
    .ob-step-dot {
      width: 34px;
      height: 34px;
    }
  }
`;

export default WithDirection(WelcomeModalWrapper);
